export enum SpeakerLayout {
    _1_0 = '1:0',
    _1_7 = '1:7',
    _1_21 = '1:21',
    _2_21 = '2:21',
    _4_0 = '4:0',
    ac = 'ac',
}

export interface PexipParticipant {
    /// A Unix timestamp of when this participant raised their hand, otherwise zero.
    buzz_time: number;

    /// Either "in" or "out" as to whether this is an inbound or outbound call.
    call_direction: 'in' | 'out';

    /// An optional call tag that is assigned to this participant.
    call_tag: string | null | undefined;

    /// Set to "YES" if the participant can be disconnected, "NO" if not.
    disconnect_supported: 'YES' | 'NO';

    /// The display name of the participant.
    display_name: string;

    /// "On" or "Off" as to whether this participant is connected via encrypted media.
    encryption: string;

    /// The UUID of an external node e.g. a Skype for Business / Lync meeting associated with an external participant. This allows grouping of external participants as the UUID will be the same for all participants associated with that external node.
    external_node_uuid: string;

    /// Set to "YES" if this participant can be sent FECC messages; "NO" if not.
    fecc_supported: 'YES' | 'NO';

    /// Boolean indicating whether the user has media capabilities.
    has_media: boolean;

    /// Set to "YES" if the call is audio only.
    is_audio_only_call: 'YES' | 'NO';

    /// Boolean indicating if it is an external participant, e.g. coming in from a Skype for Business / Lync meeting.
    is_external: boolean;

    /// Set to "YES" if the participant is administratively muted.
    is_muted: 'YES' | 'NO';

    /// Set to "YES" if the participant is the current presenter.
    is_presenting: 'YES' | 'NO';

    /// Boolean indicating whether this is a streaming/recording participant.
    is_streaming_conference: boolean;

    /// Set to "YES" if the call has video capability.
    is_video_call: 'YES' | 'NO';

    /// The calling or "from" alias. This is the alias that the recipient would use to return the call.
    local_alias: string | null | undefined;

    /// Set to "YES" if the participant can be muted, "NO" if not.
    mute_supported: 'YES' | 'NO';

    /// Text that may be used as an alternative to display_name as the participant name overlay text.
    overlay_text: string | null | undefined;

    /// The call protocol.
    ///
    /// Values: "api", "webrtc", "sip", "rtmp", "h323" or "mssip".
    ///
    /// (Note that the protocol is always reported as "api" when an Infinity Connect client dials in to Pexip Infinity.)
    ///
    /// Note: Empty for "guest" role.
    protocol:
        'api' | 'webrtc' | 'sip' | 'rtmp' | 'h323' | 'mssip' | null | undefined;

    /// The level of privileges the participant has in the conference:
    /// "chair" or "HOST": the participant has Host privileges
    /// "guest" or "GUEST": the participant has Guest privileges
    role: 'HOST' | 'GUEST' | 'chair' | 'guest';

    /// Set to "ALLOW" if the participant is administratively allowed to receive presentation, or "DENY" if disallowed.
    rx_presentation_policy: 'ALLOW' | 'DENY';

    /// The service type:
    ///
    /// "connecting": for a dial-out participant that has not been answered
    /// "waiting_room": if waiting to be allowed to join a locked conference
    /// "ivr": if on the PIN entry screen
    /// "conference": if in a VMR
    /// "lecture" if in a Virtual Auditorium
    /// "gateway": if it is a gateway call
    /// "test_call": if it is a Test Call Service
    service_type:
        | 'connecting'
        | 'waiting_room'
        | 'ivr'
        | 'conference'
        | 'lecture'
        | 'gateway'
        | 'test_call';

    /// A Unix timestamp of when this participant was spotlighted, if spotlight is used.
    spotlight: number | null | undefined;

    /// A Unix timestamp of when this participant joined (UTC).
    start_time: number;

    /// Set to "YES" if this participant can be transferred into another VMR; "NO" if not.
    transfer_supported: 'YES' | 'NO';

    /// The UUID of this participant, to use with other operations.
    uuid: string;

    /// The URI of the participant.
    ///
    /// Note: Empty for "guest" role.
    uri: string | null | undefined;

    /// The vendor identifier of the browser/endpoint with which the participant is connecting.
    ///
    /// Note: Empty for "guest" role.
    vendor: string | null | undefined;
}

export interface PexipParticipantStage {
    /// The UUID of the participant.
    participant_uuid: string;

    /// The index of the participant on the "stage". 0 is most recent speaker, 1 is the next most recent etc.
    stage_index: number;

    /// Audio speaking indication. 0 = not speaking, 100 = speaking.
    vad: number;
}

export interface PexipMessage {
    /// Name of the sending participant.
    origin: string;

    /// UUID of the sending participant.
    uuid: string;

    /// MIME content-type of the message, usually text/plain.
    type: string;

    /// Message contents.
    payload: string;
}

export interface PexRTC {
    // eslint-disable-next-line @typescript-eslint/no-misused-new
    new (): PexRTC;
    onLog(...args: unknown[]): void;
    addCall(callType: string | null): void;
    makeCall(
        node: string,
        conference: string,
        name: string,
        bandwidth: string | null,
        callType: string | null,
        flash: string | null,
    ): void;
    onSetup(
        stream: MediaStream | string,
        pinStatus: string,
        conferenceExtension: string | undefined,
    ): void;
    onError(err: string): void;
    connect(pin: number, extension?: string): void;
    disconnect(): void;
    onConnect(stream: MediaStream | string): void;
    onDisconnect(reason: string): void;
    muteAudio(setting?: boolean): void;
    muteVideo(setting?: boolean): void;
    present(call_type: 'screen' | null): void;
    renegotiate(resend_sdb?: boolean): void;
    sendChatMessage(message: string): void;
    onChatMessage(message: PexipMessage): void;
    setBuzz(): void;
    clearBuzz(uuid: string): void;
    clearAllBuzz(): void;

    transformLayout(transform: {
        layout?: SpeakerLayout;
        streaming_indicator?: boolean;
        recording_indicator?: boolean;
        enable_overlay_text?: boolean;
        enable_active_speaker_indication?: boolean;
        free_form_overlay_text?: Array<string>;
    }): void;

    uuid: string | null;
    role: 'HOST' | 'GUEST' | 'chair' | 'guest' | null;
    audio_source: null | unknown;
    video_source: null | unknown;

    getParticipants(
        callback: (participants: Array<PexipParticipant>) => void,
    ): void;

    getPresentation(): void;

    onParticipantCreate:
        ((participant: PexipParticipant) => void) | undefined | null;
    onParticipantUpdate:
        ((participant: PexipParticipant) => void) | undefined | null;
    onParticipantDelete:
        ((participant: { uuid: string }) => void) | undefined | null;

    onPresentation:
        | ((
              setting: boolean,
              presenterName: string | null,
              presenterUUID: string,
          ) => void)
        | undefined
        | null;

    onPresentationConnected:
        // Modern browsers return MediaStream, some old browsers may return URL
        ((stream: MediaStream | URL) => void) | null | undefined;
    onPresentationStopped: ((reason: string) => void) | null | undefined;
    onPresentationDisconnected: ((reason: string) => void) | null | undefined;

    onScreenshareConnected:
        // Modern browsers return MediaStream, some old browsers may return URL
        ((stream: MediaStream | URL) => void) | null | undefined;
    onScreenshareStopped: ((reason: string) => void) | null | undefined;

    onStageUpdate:
        | ((participantStages: PexipParticipantStage[]) => void)
        | undefined
        | null;
}
